import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE } from '../utils/api';
import { Box, Card, CardContent, Typography, Alert, Button } from '@mui/material';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from 'recharts';

interface Facility {
  id: number;
  name: string;
  renewal_date?: string;
}

interface Product {
  id: number;
  name: string;
  listing_valid_until?: string;
}

interface AdverseEvent {
  id: number;
  product_id: number;
  is_serious: boolean;
  sae_deadline?: string;
  status: string;
}

// Minimal shape from /adverse-events/enriched
interface EnrichedAdverseEvent {
  id: number;
  is_serious?: boolean;
  sae_deadline?: string | null;
}

interface SafetyDocument {
  id: number;
  product_id: number;
  created_at: string;
}

interface Task {
  id: number | string;
  title: string;
  description: string;
  task_type: string;
  priority: string;
  status: string;
  due_date: string;
  related_entity_type: string;
  related_entity_id: number;
  created_at: string;
}

interface DashboardStats {
  totalFacilities: number;
  totalProducts: number;
  totalAdverseEvents: number;
  seriousAdverseEvents: number;
  overdueSAEs: number;
  urgentSAEs: number;
  totalSafetyDocuments: number;
  upcomingRenewals: Facility[];
  upcomingListings: Product[];
  recentAdverseEvents: AdverseEvent[];
  recentSafetyDocuments: SafetyDocument[];
  // New task management fields
  totalTasks: number;
  pendingTasks: number;
  overdueTasks: number;
  myPendingTasks: number;
}

const DashboardOverview: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<any | null>(null);
  const [drilldown, setDrilldown] = useState<{ rows: any[]; page: number; title: string } | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const maxRetries = 5;
  const mountedRef = useRef(true);

  // Prepare data for charts
  const severityData = Object.entries(analytics?.ae_by_severity || {}).map(([name, value]) => ({ name, value: Number(value) }));
  const tsData = (analytics?.ae_30d_timeseries || []).map((r: any) => ({ date: r.date, total: r.total }));

  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    
    if (!token || !user) {
      setError('Please log in to view the dashboard');
      setLoading(false);
      return;
    }
    
    const controller = new AbortController();
    const signal = controller.signal;

    mountedRef.current = true;

    const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));

    const loadWithRetry = async () => {
      let attempt = 0;
      setRetryCount(0);
      setIsRetrying(false);
      while (attempt <= maxRetries) {
        try {
          await fetchDashboardData(signal);
          // success, break
          return;
        } catch (e) {
          // fetchDashboardData throws only when we rethrow; otherwise error state is set internally
        }
        attempt += 1;
        if (attempt > maxRetries) break;
        if (!mountedRef.current) break;
        setIsRetrying(true);
        setRetryCount(attempt);
        // exponential backoff: 800ms, 1600ms, 3200ms
        const waitMs = 800 * Math.pow(2, attempt - 1);
        await sleep(waitMs);
      }
      // After retries, if still failing and no stats, show error
      setIsRetrying(false);
      setLoading(false);
      if (!stats) {
        setError('Unable to connect to the server. Please ensure the backend is running.');
      }
    };

    loadWithRetry();
    return () => {
      mountedRef.current = false;
      controller.abort();
    };
  }, []);

  const fetchDashboardData = async (signal?: AbortSignal) => {
    try {
      setError(null);

      // Get the authentication token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Try new aggregated endpoint first
      const overviewRes = await fetch(`${API_BASE}/dashboard/overview`, { headers, signal, cache: 'no-store', keepalive: true as any });
      if (!overviewRes.ok) throw new Error(`Overview: ${overviewRes.status} ${overviewRes.statusText}`);
      const overview = await overviewRes.json();
      setAnalytics(overview.analytics || {});

      const now = new Date();
      const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

      // Fetch minimal enriched list to derive SAE submission tasks (keep small if backend supports pagination)
      let events: EnrichedAdverseEvent[] = [];
      try {
        const enr = await fetch(`${API_BASE}/adverse-events/enriched?limit=200`, { headers, signal, cache: 'no-store', keepalive: true as any });
        if (enr.ok) {
          const body = await enr.json();
          events = Array.isArray(body) ? body : (body?.data ?? []);
        }
      } catch {}

      // Create SAE submission tasks for upcoming deadlines
      const saeSubmissionTasks: Task[] = events
        .filter((ae: EnrichedAdverseEvent) => ae.is_serious && ae.sae_deadline)
        .map((ae: EnrichedAdverseEvent) => ({
          id: `sae-${ae.id}`,
          title: `SAE Report Submission - Event #${ae.id}`,
          description: `Submit serious adverse event report to FDA for Event #${ae.id}`,
          status: 'pending',
          priority: 'high',
          due_date: (ae.sae_deadline || '') as string,
          task_type: 'sae_submission',
          related_entity_type: 'adverse_event',
          related_entity_id: ae.id,
          created_at: now.toISOString()
        })) as Task[];

      // Combine regular tasks with SAE submission tasks
      // Load user tasks
      let myTasksResp: Task[] = [];
      try {
        const res = await fetch(`${API_BASE}/my-tasks/`, { headers, signal, cache: 'no-store', keepalive: true as any });
        if (res.ok) {
          const raw = await res.json();
          myTasksResp = Array.isArray(raw)
            ? raw.map((t: any) => ({
                id: t.id,
                title: t.title,
                description: t.description,
                task_type: t.task_type,
                priority: t.priority,
                status: t.status,
                due_date: String(t.due_date ?? ''),
                related_entity_type: t.related_entity_type,
                related_entity_id: Number(t.related_entity_id ?? 0),
                created_at: String(t.created_at ?? new Date().toISOString()),
              }))
            : [];
        }
        else {
          const pub = await fetch(`${API_BASE}/my-tasks/public`, { signal });
          if (pub.ok) {
            const rawPub = await pub.json();
            myTasksResp = Array.isArray(rawPub)
              ? rawPub.map((t: any) => ({
                  id: t.id,
                  title: t.title,
                  description: t.description,
                  task_type: t.task_type,
                  priority: t.priority,
                  status: t.status,
                  due_date: String(t.due_date ?? ''),
                  related_entity_type: t.related_entity_type,
                  related_entity_id: Number(t.related_entity_id ?? 0),
                  created_at: String(t.created_at ?? new Date().toISOString()),
                }))
              : [];
          }
        }
      } catch {}
      const allTasks: Task[] = [...myTasksResp, ...saeSubmissionTasks];

      const counts = overview?.counts || {};
      const dashboardStats: DashboardStats = {
        totalFacilities: Number(counts.facilities || 0),
        totalProducts: Number(counts.products || 0),
        totalAdverseEvents: Number(counts.adverse_events || 0),
        seriousAdverseEvents: Number(counts.serious_adverse_events || 0),
        overdueSAEs: overview?.analytics?.overdue_saes ?? 0,
        urgentSAEs: overview?.analytics?.due_5d_saes ?? 0,
        totalSafetyDocuments: Number(counts.safety_documents || 0),
        upcomingRenewals: (overview?.upcoming_renewals || []) as Facility[],
        upcomingListings: (overview?.upcoming_listings || []) as Product[],
        recentAdverseEvents: (overview?.recent_adverse_events || []) as any,
        recentSafetyDocuments: (overview?.recent_safety_documents || []) as any,
        totalTasks: overview?.analytics?.tasks?.total ?? allTasks.length,
        pendingTasks: overview?.analytics?.tasks?.pending ?? allTasks.filter((t: Task) => t.status === 'pending').length,
        overdueTasks: overview?.analytics?.tasks?.overdue ?? allTasks.filter((t: Task) => t.status === 'overdue').length,
        myPendingTasks: overview?.analytics?.tasks?.pending ?? allTasks.filter((t: Task) => t.status === 'pending').length
      };

      setStats(dashboardStats);
      setTasks(allTasks); // Update tasks state with combined tasks
      setIsRetrying(false);
    } catch (error) {
      console.error('Overview load failed, falling back. Reason:', error);
      // Fallback to previous multi-call strategy to preserve functionality
      // Get the authentication token
      const token = localStorage.getItem('token');
      if (!token) throw error;
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      const [facilities, products, enrichedEvents, safetyDocuments, myTasksRaw, analyticsResp] = await Promise.all([
        fetch(`${API_BASE}/facilities/`, { headers, signal }).then(res => { if (!res.ok) throw new Error(`Facilities: ${res.status}`); return res.json(); }),
        fetch(`${API_BASE}/products/`, { headers, signal }).then(res => { if (!res.ok) throw new Error(`Products: ${res.status}`); return res.json(); }),
        fetch(`${API_BASE}/adverse-events/enriched`, { headers, signal }).then(res => { if (!res.ok) throw new Error(`Adverse Events (enriched): ${res.status}`); return res.json(); }),
        fetch(`${API_BASE}/safety-documents/`, { headers, signal }).then(res => { if (!res.ok) throw new Error(`Safety Documents: ${res.status}`); return res.json(); }),
        (async () => { const r = await fetch(`${API_BASE}/my-tasks/`, { headers, signal }); if (!r.ok) { const pub = await fetch(`${API_BASE}/my-tasks/public`, { signal }); if (!pub.ok) return []; return pub.json(); } return r.json(); })(),
        fetch(`${API_BASE}/dashboard/analytics`, { headers, signal }).then(res => { if (!res.ok) throw new Error(`Analytics: ${res.status}`); return res.json(); }),
      ]);
      setAnalytics(analyticsResp);
      const now = new Date();
      const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      const events: EnrichedAdverseEvent[] = Array.isArray(enrichedEvents) ? enrichedEvents : (enrichedEvents?.data ?? []);
      const saeSubmissionTasks: Task[] = events
        .filter((ae: EnrichedAdverseEvent) => ae.is_serious && ae.sae_deadline)
        .map((ae: EnrichedAdverseEvent) => ({
          id: `sae-${ae.id}`,
          title: `SAE Report Submission - Event #${ae.id}`,
          description: `Submit serious adverse event report to FDA for Event #${ae.id}`,
          status: 'pending',
          priority: 'high',
          due_date: String(ae.sae_deadline ?? ''),
          task_type: 'sae_submission',
          related_entity_type: 'adverse_event',
          related_entity_id: ae.id,
          created_at: now.toISOString()
        })) as Task[];
      const myTasksNorm: Task[] = Array.isArray(myTasksRaw)
        ? myTasksRaw.map((t: any) => ({
            id: t.id,
            title: t.title,
            description: t.description,
            task_type: t.task_type,
            priority: t.priority,
            status: t.status,
            due_date: String(t.due_date ?? ''),
            related_entity_type: t.related_entity_type,
            related_entity_id: Number(t.related_entity_id ?? 0),
            created_at: String(t.created_at ?? new Date().toISOString()),
          }))
        : [];
      const allTasks: Task[] = [...myTasksNorm, ...saeSubmissionTasks];
      const dashboardStats: DashboardStats = {
        totalFacilities: facilities.length,
        totalProducts: products.length,
        totalAdverseEvents: events.length,
        seriousAdverseEvents: events.filter((ae: EnrichedAdverseEvent) => !!ae.is_serious).length,
        overdueSAEs: analyticsResp.overdue_saes ?? 0,
        urgentSAEs: analyticsResp.due_5d_saes ?? 0,
        totalSafetyDocuments: safetyDocuments.length,
        upcomingRenewals: facilities.filter((f: Facility) => { if (!f.renewal_date) return false; const rd = new Date(f.renewal_date); return rd > now && rd <= thirtyDaysFromNow; }),
        upcomingListings: products.filter((p: Product) => { if (!p.listing_valid_until) return false; const ld = new Date(p.listing_valid_until); return ld > now && ld <= thirtyDaysFromNow; }),
        recentAdverseEvents: (events || []).slice(0, 5).map((e: EnrichedAdverseEvent) => ({ id: e.id, product_id: 0, is_serious: !!e.is_serious, sae_deadline: e.sae_deadline || undefined, status: '' })),
        recentSafetyDocuments: safetyDocuments.slice(0, 5),
        totalTasks: analyticsResp?.tasks?.total ?? allTasks.length,
        pendingTasks: analyticsResp?.tasks?.pending ?? allTasks.filter((t: Task) => t.status === 'pending').length,
        overdueTasks: analyticsResp?.tasks?.overdue ?? allTasks.filter((t: Task) => t.status === 'overdue').length,
        myPendingTasks: analyticsResp?.tasks?.pending ?? allTasks.filter((t: Task) => t.status === 'pending').length
      };
      setStats(dashboardStats);
      setTasks(allTasks);
      // Rethrow to allow outer caller to stop retrying since fallback succeeded
      return;
    } finally {
      setLoading(false);
    }
  };

  const fetchDrilldown = useCallback(async (kind: 'due_sae_overdue' | 'due_sae_due' | 'incomplete_fac' | 'incomplete_prod') => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const headers: any = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
      let url = '';
      let title = '';
      if (kind === 'due_sae_overdue') {
        url = `${API_BASE}/dashboard/mocra/due-cases?kind=sae&status=overdue`;
        title = 'Overdue SAE Cases (15 days)';
      } else if (kind === 'due_sae_due') {
        url = `${API_BASE}/dashboard/mocra/due-cases?kind=sae&status=due`;
        title = 'SAE Cases Due (within 15 days)';
      } else if (kind === 'incomplete_fac') {
        url = `${API_BASE}/dashboard/mocra/incomplete-facilities`;
        title = 'Incomplete Facilities';
      } else if (kind === 'incomplete_prod') {
        url = `${API_BASE}/dashboard/mocra/incomplete-products`;
        title = 'Incomplete Products';
      }
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error(`Drilldown: ${res.status} ${res.statusText}`);
      const data = await res.json();
      const rows = Array.isArray(data) ? data : (data.data || []);
      setDrilldown({ rows, page: 1, title });
    } catch (e: any) {
      console.error(e);
      setError('Failed to load drilldown. Please try again.');
    }
  }, []);

  const exportCSV = useCallback(() => {
    if (!drilldown || !drilldown.rows.length) return;
    const headers = Object.keys(drilldown.rows[0]);
    const csv = [headers.join(','), ...drilldown.rows.map((r:any) => headers.map(h => JSON.stringify(r[h] ?? '')).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'drilldown.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, [drilldown]);

  // fetchTasks removed; task loading handled within fetchDashboardData

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    return isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
  };

  const getDaysUntil = (dateString: string) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 0;
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Sort tasks by due date ascending (nulls last), then created_at desc
  const sortedTasks = React.useMemo(() => {
    const copy = [...tasks];
    copy.sort((a, b) => {
      const aDue = a.due_date ? new Date(a.due_date).getTime() : Number.POSITIVE_INFINITY;
      const bDue = b.due_date ? new Date(b.due_date).getTime() : Number.POSITIVE_INFINITY;
      if (aDue !== bDue) return aDue - bDue;
      const aCreated = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bCreated = b.created_at ? new Date(b.created_at).getTime() : 0;
      return bCreated - aCreated;
    });
    return copy;
  }, [tasks]);

  if (loading && !error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <h2 className="text-lg font-semibold text-gray-700">Loading Dashboard...</h2>
          <p className="text-gray-500 mt-2">Please wait while we fetch your compliance data</p>
          {isRetrying && (
            <p className="text-xs text-gray-500 mt-1">Reconnecting... attempt {retryCount + 1} of {maxRetries + 1}</p>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-800 mb-2">Connection Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => fetchDashboardData()}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-gray-700">No Data Available</h2>
          <p className="text-gray-500 mt-2">Start by adding facilities and products to see your dashboard</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Compliance Dashboard</h1>
        <p className="text-gray-600">Monitor your MoCRA compliance status and critical deadlines</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="bg-gray-800 rounded-lg shadow p-6 border-l-4 border-indigo-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-indigo-900/40 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-indigo-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-300">Facilities</p>
              <p className="text-2xl font-bold text-gray-100">{stats.totalFacilities}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg shadow p-6 border-l-4 border-green-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-green-900/40 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-300">Products</p>
              <p className="text-2xl font-bold text-gray-100">{stats.totalProducts}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg shadow p-6 border-l-4 border-red-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-red-900/40 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-300">SAEs</p>
              <p className="text-2xl font-bold text-gray-100">{stats.seriousAdverseEvents}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg shadow p-6 border-l-4 border-blue-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-blue-900/40 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-300">Safety Docs</p>
              <p className="text-2xl font-bold text-gray-100">{stats.totalSafetyDocuments}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg shadow p-6 border-l-4 border-purple-500">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-purple-900/40 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-300">My Tasks</p>
              <p className="text-2xl font-bold text-gray-100">{stats.myPendingTasks}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Severity Distribution & 30-day Trend (modern charts) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="shadow" sx={{ bgcolor: '#1f2937', color: '#e5e7eb' }}>
          <CardContent>
            <Typography variant="h6" className="mb-4" sx={{ color: '#e5e7eb' }}>Event Severity Mix</Typography>
            <Box sx={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={severityData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#9ca3af' }} stroke="#374151" />
                  <YAxis allowDecimals={false} tick={{ fill: '#9ca3af' }} stroke="#374151" />
                  <Tooltip contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', color: '#e5e7eb' }} />
                  <Bar dataKey="value" fill="#6366f1" name="Count" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
        <Card className="shadow" sx={{ bgcolor: '#1f2937', color: '#e5e7eb' }}>
          <CardContent>
            <Typography variant="h6" className="mb-4" sx={{ color: '#e5e7eb' }}>Last 30 Days - Events</Typography>
            <Box sx={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={tsData}>
                  <defs>
                    <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#9ca3af' }} stroke="#374151" />
                  <YAxis allowDecimals={false} tick={{ fill: '#9ca3af' }} stroke="#374151" />
                  <Tooltip contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151', color: '#e5e7eb' }} />
                  <Area type="monotone" dataKey="total" stroke="#3b82f6" fillOpacity={1} fill="url(#colorTotal)" />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </div>

      {/* MoCRA completeness quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="shadow" sx={{ bgcolor: '#1f2937', color: '#e5e7eb' }}>
          <CardContent>
            <div className="flex justify-between items-center">
              <div>
                <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#e5e7eb' }}>Incomplete Facilities</Typography>
                <Typography variant="body2" sx={{ color: '#9ca3af' }}>Resolve missing data for facility registrations</Typography>
              </div>
              <Button variant="outlined" color="inherit" onClick={() => fetchDrilldown('incomplete_fac')}>View list</Button>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow" sx={{ bgcolor: '#1f2937', color: '#e5e7eb' }}>
          <CardContent>
            <div className="flex justify-between items-center">
              <div>
                <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#e5e7eb' }}>Incomplete Products</Typography>
                <Typography variant="body2" sx={{ color: '#9ca3af' }}>Finish listings to maintain compliance</Typography>
              </div>
              <Button variant="outlined" color="inherit" onClick={() => fetchDrilldown('incomplete_prod')}>View list</Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Drilldown modal */}
      {drilldown && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg shadow-lg w-full max-w-5xl p-6 text-gray-100">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">{drilldown.title}</h3>
              <div className="space-x-3">
                <button className="text-sm text-gray-200 hover:text-white" onClick={exportCSV}>Export CSV</button>
                <button className="text-sm text-gray-400 hover:text-gray-200" onClick={() => setDrilldown(null)}>Close</button>
              </div>
            </div>
            <div className="overflow-auto max-h-[60vh]">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-300">
                    {drilldown.rows.length > 0 && Object.keys(drilldown.rows[0]).map(h => (
                      <th key={h} className="px-3 py-2 border-b border-gray-700">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {drilldown.rows.slice((drilldown.page-1)*20, (drilldown.page)*20).map((row:any, idx:number) => (
                    <tr key={idx} className="border-b border-gray-700">
                      {Object.keys(drilldown.rows[0] || {}).map(h => (
                        <td key={h} className="px-3 py-2 text-gray-200">{String(row[h] ?? '')}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-between items-center mt-4">
              <button
                className="text-sm px-3 py-1 bg-gray-700 text-gray-200 rounded"
                disabled={drilldown.page <= 1}
                onClick={() => setDrilldown(d => d ? { ...d, page: d.page - 1 } : d)}
              >Prev</button>
              <div className="text-xs text-gray-300">Page {drilldown.page}</div>
              <button
                className="text-sm px-3 py-1 bg-gray-700 text-gray-200 rounded"
                disabled={drilldown.page * 20 >= drilldown.rows.length}
                onClick={() => setDrilldown(d => d ? { ...d, page: d.page + 1 } : d)}
              >Next</button>
            </div>
          </div>
        </div>
      )}

      {/* Alerts */}
      {(stats.overdueSAEs > 0 || stats.urgentSAEs > 0) && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Critical Alerts</h2>
          {stats.overdueSAEs > 0 && (
            <Alert severity="error" variant="filled" onClick={() => fetchDrilldown('due_sae_overdue')} sx={{ cursor: 'pointer' }}>
              {stats.overdueSAEs} SAE(s) Overdue — immediate action required
            </Alert>
          )}
          {stats.urgentSAEs > 0 && (
            <Alert severity="warning" variant="filled" onClick={() => fetchDrilldown('due_sae_due')} sx={{ cursor: 'pointer' }}>
              {stats.urgentSAEs} SAE(s) due soon — review and submit
            </Alert>
          )}
        </div>
      )}

      {/* Upcoming Deadlines */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-100 mb-4">Upcoming Renewals</h3>
          {stats.upcomingRenewals.length > 0 ? (
            <div className="space-y-3">
              {stats.upcomingRenewals.map((facility) => (
                <div key={facility.id} className="flex justify-between items-center p-3 bg-gray-700 rounded-lg">
                  <div>
                    <span className="text-sm font-medium text-gray-100">{facility.name}</span>
                    <p className="text-xs text-gray-300">Facility renewal</p>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-medium text-gray-100">
                      {formatDate(facility.renewal_date!)}
                    </span>
                    <p className="text-xs text-gray-300">
                      {getDaysUntil(facility.renewal_date!)} days remaining
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <svg className="mx-auto h-8 w-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-sm text-gray-300 mt-2">No upcoming renewals</p>
            </div>
          )}
        </div>

        <div className="bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-100 mb-4">Recent Adverse Events</h3>
          {stats.recentAdverseEvents.length > 0 ? (
            <div className="space-y-3">
              {stats.recentAdverseEvents.map((event) => (
                <div key={event.id} className="flex justify-between items-center p-3 bg-gray-700 rounded-lg">
                  <div>
                    <span className="text-sm font-medium text-gray-100">Event #{event.id}</span>
                    <p className="text-xs text-gray-300">Product event</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      event.is_serious ? 'bg-red-900/40 text-red-300' : 'bg-green-900/40 text-green-300'
                    }`}>
                      {event.is_serious ? 'SAE' : 'Non-SAE'}
                    </span>
                    {event.is_serious && event.sae_deadline && (() => {
                      const days = getDaysUntil(event.sae_deadline!);
                      const cls = days < 0
                        ? 'bg-red-900/40 text-red-300'
                        : (days <= 5 ? 'bg-yellow-900/40 text-yellow-300' : 'bg-gray-900/40 text-gray-300');
                      return (
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${cls}`}>
                          Due {formatDate(event.sae_deadline!)} ({days}d)
                        </span>
                      );
                    })()}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <svg className="mx-auto h-8 w-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-gray-300 mt-2">No recent adverse events</p>
            </div>
          )}
        </div>
      </div>

      {/* Task Management Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">My Tasks</h2>
          <a
            href="/dashboard?tab=workflow#tasks"
            className="text-sm text-indigo-400 hover:text-indigo-300"
            title="View all tasks in Workflow"
          >View All</a>
        </div>
        <div className="bg-gray-800 rounded-lg shadow p-6">
          {stats && stats.myPendingTasks > 0 ? (
            <div className="space-y-3">
              {sortedTasks.slice(0, 5).map((task) => (
                <div key={task.id} className="flex justify-between items-center p-3 bg-gray-700 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-gray-100">{task.title}</span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        task.priority === 'urgent' ? 'bg-red-900/40 text-red-300' :
                        task.priority === 'high' ? 'bg-orange-900/40 text-orange-300' :
                        task.priority === 'normal' ? 'bg-blue-900/40 text-blue-300' :
                        'bg-green-900/40 text-green-300'
                      }`}>
                        {task.priority}
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        task.status === 'overdue' ? 'bg-red-900/40 text-red-300' :
                        task.status === 'pending' ? 'bg-yellow-900/40 text-yellow-300' :
                        task.status === 'in_progress' ? 'bg-blue-900/40 text-blue-300' :
                        'bg-green-900/40 text-green-300'
                      }`}>
                        {task.status}
                      </span>
                    </div>
                    <p className="text-xs text-gray-300">{task.description}</p>
                    {task.due_date && (
                      <p className="text-xs text-gray-300 mt-1">
                        Due: {formatDate(task.due_date)} ({getDaysUntil(task.due_date)} days)
                      </p>
                    )}
                  </div>
                </div>
              ))}
              {stats && stats.myPendingTasks > 5 && (
                <div className="text-center py-2">
                  <p className="text-sm text-gray-300">+{stats.myPendingTasks - 5} more tasks</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6">
              <svg className="mx-auto h-8 w-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <p className="text-sm text-gray-300 mt-2">No pending tasks</p>
              <p className="text-xs text-gray-400">You're all caught up!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview; 